import React, { useState, useEffect } from 'react';
import './CreativeModal.css';
import api from '../../services/api';
import { getSecureMediaUrl, clearMediaCache } from '../../utils/secureMedia';

const EditCreativeModal = ({ isOpen, creative, onClose, onUpdate }) => {
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    brand: '',
    category: '',
    form: '',
    contract_external_id: '',
    kktu_code: '',
    target_audience: '',
    creative_urls: [],
    creative_text: '',
    media_files: [],
    ad_types: {
      self_promotion: false,
      social_ad_quota: false,
      social_ad: false,
      cobranding: false
    },
    telegram_post_url: '',
    vk_post_url: ''
  });
  const [mediaFilesInfo, setMediaFilesInfo] = useState({});
  const [imageErrors, setImageErrors] = useState({}); // Отслеживаем ошибки загрузки изображений
  const [mediaBlobUrls, setMediaBlobUrls] = useState({}); // 🔒 Безопасные blob URLs
  const [creativeTypes, setCreativeTypes] = useState([]);
  const [kktuList, setKktuList] = useState([]);
  const [contracts, setContracts] = useState([]);
  const [viewsData, setViewsData] = useState(null);
  const [loadingViews, setLoadingViews] = useState(false);
  const [loading, setLoading] = useState(false);

  // Устанавливаем loading в true сразу при открытии модального окна с креативом
  useEffect(() => {
    if (isOpen && creative && creative.erid) {
      setLoading(true);
      // Очищаем форму сразу при открытии
      setFormData({
        name: '',
        description: '',
        brand: '',
        category: '',
        form: '',
        contract_external_id: '',
        kktu_code: '',
        target_audience: '',
        creative_urls: [],
        creative_text: '',
        media_files: [],
        ad_types: {
          self_promotion: false,
          social_ad_quota: false,
          social_ad: false,
          cobranding: false
        },
        telegram_post_url: '',
        vk_post_url: ''
      });
      setMediaFilesInfo({});
      setImageErrors({});
      setViewsData(null);
    }
  }, [isOpen, creative?.erid]);

  useEffect(() => {
    const loadCreativeFromVKORD = async () => {
      if (!creative || !creative.erid || !isOpen) return;
      
      // Убеждаемся, что loading установлен в true
      setLoading(true);
      const loadingStartTime = Date.now();
      
      // Даем React время отрендерить индикатор загрузки перед началом загрузки данных
      await new Promise(resolve => setTimeout(resolve, 300));
      
      try {
        // Загружаем актуальные данные из VK ORD
        const response = await api.get(`/creatives/vk-ord/${creative.erid}`);
        const vkCreative = response.data;
        
        // Заполняем форму данными из VK ORD
        // ВАЖНО: нормализуем form в формат с подчеркиваниями (как в справочнике)
        // Если приходит с дефисами - конвертируем в подчеркивания
        const normalizeForm = (formValue) => {
          if (!formValue) return '';
          const hasHyphens = formValue.includes('-');
          return hasHyphens ? formValue.replace(/-/g, '_') : formValue;
        };
        
        const rawForm = vkCreative.form || creative.form;
        const normalizedForm = normalizeForm(rawForm);
        const finalForm = normalizedForm;
        
        const newFormData = {
          name: vkCreative.name || creative.name || '',
          description: vkCreative.description || creative.description || '',
          brand: vkCreative.brand || creative.brand || '',
          category: vkCreative.category || creative.category || '',
          form: finalForm,
          contract_external_id: vkCreative.contract_external_id || creative.contract_external_id || '',
          kktu_code: (vkCreative.kktus && vkCreative.kktus.length > 0) ? vkCreative.kktus[0] : '',
          target_audience: vkCreative.targeting || creative.targeting || '',
          creative_urls: vkCreative.target_urls || creative.target_urls || [],
          creative_text: (vkCreative.texts && vkCreative.texts.length > 0) ? vkCreative.texts[0] : '',
          media_files: vkCreative.media_external_ids || [],
          ad_types: creative.ad_types || {
            self_promotion: false,
            social_ad_quota: false,
            social_ad: false,
            cobranding: false
          },
          telegram_post_url: creative.telegram_post_url || '',
          vk_post_url: creative.vk_post_url || ''
        };
        
        setFormData(newFormData);
        
        // Принудительная нормализация через небольшую задержку (на случай если данные установились раньше справочников)
        setTimeout(() => {
          setFormData(prev => {
            if (!prev.form) return prev;
            const currentForm = prev.form;
            if (currentForm.includes('-')) {
              const normalizedForm = currentForm.replace(/-/g, '_');
              return { ...prev, form: normalizedForm };
            }
            return prev;
          });
        }, 200);
        
      } catch (error) {
        console.error('❌ Ошибка загрузки из VK ORD, используем локальные данные:', error);
        
        // Fallback: используем данные из локальной БД
        // ВАЖНО: нормализуем form в формат с подчеркиваниями (как в справочнике)
        // Если приходит с дефисами - конвертируем в подчеркивания
        const normalizeForm = (formValue) => {
          if (!formValue) return '';
          return formValue.includes('-') ? formValue.replace(/-/g, '_') : formValue;
        };
        
        const rawForm = creative.form;
        const normalizedForm = normalizeForm(rawForm);
        
        const fallbackFormData = {
          name: creative.name || '',
          description: creative.description || '',
          brand: creative.brand || '',
          category: creative.category || '',
          form: normalizedForm,
          contract_external_id: creative.contract_external_id || '',
          kktu_code: (creative.kktus && creative.kktus.length > 0) ? creative.kktus[0] : '',
          target_audience: creative.targeting || '',
          creative_urls: creative.target_urls || [],
          creative_text: (creative.texts && creative.texts.length > 0) ? creative.texts[0] : '',
          media_files: creative.media_files || creative.media_external_ids || [],
          ad_types: creative.ad_types || {
            self_promotion: false,
            social_ad_quota: false,
            social_ad: false,
            cobranding: false
          },
          telegram_post_url: creative.telegram_post_url || '',
          vk_post_url: creative.vk_post_url || ''
        };
        
        setFormData(fallbackFormData);
        
        // Принудительная нормализация через небольшую задержку
        setTimeout(() => {
          setFormData(prev => {
            if (!prev.form) return prev;
            const currentForm = prev.form;
            if (currentForm.includes('-')) {
              const normalizedForm = currentForm.replace(/-/g, '_');
              return { ...prev, form: normalizedForm };
            }
            return prev;
          });
        }, 200);
      } finally {
        // Гарантируем, что индикатор загрузки показывается минимум 500ms
        const minLoadingTime = 500;
        const elapsed = Date.now() - loadingStartTime;
        const remainingTime = Math.max(0, minLoadingTime - elapsed);
        if (remainingTime > 0) {
          await new Promise(resolve => setTimeout(resolve, remainingTime));
        }
        setLoading(false);
      }
    };
    
    if (creative && creative.erid && isOpen) {
      loadCreativeFromVKORD();
    }
  }, [creative?.erid, isOpen]);

  // Сброс состояния загрузки при закрытии модального окна
  useEffect(() => {
    if (!isOpen) {
      setLoading(false);
      // Очищаем форму при закрытии
      setFormData({
        name: '',
        description: '',
        brand: '',
        category: '',
        form: '',
        contract_external_id: '',
        kktu_code: '',
        target_audience: '',
        creative_urls: [],
        creative_text: '',
        media_files: [],
        ad_types: {
          self_promotion: false,
          social_ad_quota: false,
          social_ad: false,
          cobranding: false
        },
        telegram_post_url: '',
        vk_post_url: ''
      });
      setMediaFilesInfo({});
      setImageErrors({});
      setViewsData(null);
    }
  }, [isOpen]);

  useEffect(() => {
    const loadDictionaries = async () => {
      if (!isOpen) return;
      
      try {
        const [typesRes, kktuRes, contractsRes] = await Promise.all([
          api.get('/dictionaries/creative-types'),
          api.get('/dictionaries/kktu'),
          api.get('/contracts/')
        ]);

        // Типы креативов
        const typesData = typesRes.data;
        const types = typesData.items || typesData;
        setCreativeTypes(types);

        // После загрузки справочников, проверяем и нормализуем form если нужно
        if (formData.form && types.length > 0) {
          const currentForm = formData.form;
          // Если значение содержит дефисы - нормализуем в подчеркивания
          if (currentForm.includes('-')) {
            const normalizedForm = currentForm.replace(/-/g, '_');
            setFormData(prev => ({ ...prev, form: normalizedForm }));
          }
        }

        // ККТУ
        const kktuData = kktuRes.data;
        const kktu = kktuData.items || kktuData;
        setKktuList(kktu);

        // Договоры
        const contractsData = contractsRes.data;
        setContracts(contractsData);

      } catch (error) {
        console.error('❌ Ошибка загрузки справочников:', error);
        if (error.response) {
          console.error('Ответ сервера:', error.response.status, error.response.data);
        }
      }
    };

    if (isOpen) {
      loadDictionaries();
    }
  }, [isOpen]);

  // 🔒 Загрузка медиафайлов через безопасный метод (Authorization заголовок)
  useEffect(() => {
    const loadMediaFiles = async () => {
      if (!formData.media_files || formData.media_files.length === 0) return;
      
      const info = {};
      const blobUrls = {};
      
      for (const fileId of formData.media_files) {
        try {
          // Загружаем информацию о файле
          const response = await api.get(`/media/info/${fileId}`);
          info[fileId] = response.data;
          
          // 🔒 БЕЗОПАСНО: Загружаем blob URL с Authorization заголовком
          const blobUrl = await getSecureMediaUrl(fileId);
          blobUrls[fileId] = blobUrl;
        } catch (error) {
          console.error(`Ошибка загрузки файла ${fileId}:`, error);
        }
      }
      
      setMediaFilesInfo(info);
      setMediaBlobUrls(blobUrls);
    };

    if (isOpen && formData.media_files.length > 0) {
      loadMediaFiles();
    }
    
    // Очищаем blob URLs при размонтировании
    return () => {
      if (!isOpen) {
        clearMediaCache();
      }
    };
  }, [isOpen, formData.media_files]);

  // Отдельный useEffect для нормализации form после загрузки справочников
  useEffect(() => {
    if (!isOpen || creativeTypes.length === 0) return;
    
    // Используем функциональное обновление, чтобы получить актуальное значение formData
    setFormData(prev => {
      if (!prev.form) return prev;
      
      const currentForm = prev.form;
      // Если значение содержит дефисы - нормализуем в подчеркивания
      if (currentForm.includes('-')) {
        const normalizedForm = currentForm.replace(/-/g, '_');
        // Обновляем только если формат отличается
        if (normalizedForm !== currentForm) {
          return { ...prev, form: normalizedForm };
        }
      }
      return prev;
    });
  }, [isOpen, creativeTypes]);

  const handleChange = (e) => {
    const { name, value, checked } = e.target;
    
    if (name.startsWith('ad_types.')) {
      const adType = name.split('.')[1];
      setFormData(prev => ({
        ...prev,
        ad_types: {
          ...prev.ad_types,
          [adType]: checked
        }
      }));
    } else {
      setFormData(prev => ({
        ...prev,
        [name]: value
      }));
    }
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    
    // form уже в правильном формате с подчеркиваниями (как в справочнике и БД)
    // Нормализация не требуется - данные уже в нужном формате
    const normalizedData = {
      ...formData
    };
    
    onUpdate(creative.erid, normalizedData);
  };

  const handleGetViews = async () => {
    if (!creative || !creative.erid) return;
    
    setLoadingViews(true);
    try {
      // Сначала сохраняем ссылки, если они изменились
      const needsSave = 
        formData.telegram_post_url !== creative.telegram_post_url ||
        formData.vk_post_url !== creative.vk_post_url;
      
      if (needsSave) {
        await api.put(`/creatives/${creative.erid}`, {
          telegram_post_url: formData.telegram_post_url,
          vk_post_url: formData.vk_post_url
        });
      }
      
      const response = await api.get(`/views/creative/${creative.erid}`);
      setViewsData(response.data);
    } catch (error) {
      console.error('Error fetching views:', error);
      alert(`Ошибка получения просмотров: ${error.message}`);
    } finally {
      setLoadingViews(false);
    }
  };

  const getCreativeTypeName = (form) => {
    if (!Array.isArray(creativeTypes) || creativeTypes.length === 0) return form;
    const type = creativeTypes.find(t => t.code === form);
    return type ? type.name : form;
  };

  const getKktuName = (code) => {
    if (!Array.isArray(kktuList) || kktuList.length === 0) return '';
    const kktu = kktuList.find(k => k.code === code);
    return kktu ? kktu.name : '';
  };

  const formatKktu = (code) => {
    const name = getKktuName(code);
    return name ? `${code} - ${name}` : code;
  };

  const getContractInfo = (externalId) => {
    if (!Array.isArray(contracts) || contracts.length === 0) return null;
    const contract = contracts.find(c => c.external_id === externalId);
    if (!contract) return null;
    
    // Формат: "№ 23 - Реклама КВН" или просто "№ 23" если нет комментария
    const displayName = contract.serial 
      ? `№ ${contract.serial}${contract.comment ? ' - ' + contract.comment : ''}`
      : (contract.comment || contract.external_id);
    
    return { name: displayName, id: contract.id };
  };

  // 🔒 БЕЗОПАСНО: Загружаем медиафайлы с Authorization заголовком
  const getMediaUrl = (fileId) => {
    // Возвращаем blob URL из кеша (загруженный через Authorization заголовок)
    return mediaBlobUrls[fileId] || null;
  };

  const isVideoFile = (fileId) => {
    // Если изображение не загрузилось (ошибка) - считаем что это видео
    if (imageErrors[fileId]) {
      return true;
    }
    
    // Проверяем по content_type из API
    const info = mediaFilesInfo[fileId];
    if (info && info.content_type) {
      return info.content_type.startsWith('video/');
    }
    
    // Если информация не загружена, проверяем по расширению в ID
    // VK ORD обычно включает расширение в ID: "abc123.mp4"
    const videoExtensions = ['.mp4', '.mov', '.avi', '.webm', '.mkv', '.m4v'];
    return videoExtensions.some(ext => fileId.toLowerCase().includes(ext));
  };

  const handleImageError = (fileId) => {
    setImageErrors(prev => ({ ...prev, [fileId]: true }));
  };

  if (!isOpen) return null;

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-content modal-large" onClick={(e) => e.stopPropagation()}>
        <div className="modal-header">
          <h2>Редактирование креатива</h2>
          <button className="modal-close" onClick={onClose}>&times;</button>
        </div>
        
        <div className="modal-body">
          {loading ? (
            <div className="loading-container" style={{ minHeight: '200px', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center' }}>
              <div className="loading-spinner"></div>
              <p style={{marginTop: '16px', color: '#586069', fontSize: '14px'}}>Загрузка данных креатива...</p>
            </div>
          ) : (
          <form onSubmit={handleSubmit}>
            {/* ERID - только для чтения */}
            <div className="form-group">
              <label>ERID (только чтение)</label>
              <input 
                type="text" 
                value={creative?.erid || ''}
                disabled
                className="form-control"
              />
            </div>

            {/* Название - редактируемое */}
            <div className="form-group">
              <label>Название *</label>
              <input 
                type="text" 
                name="name"
                value={formData.name}
                onChange={handleChange}
                className="form-control"
                required
              />
            </div>

            {/* Вид рекламы - редактируемые чекбоксы */}
            <div className="form-group">
              <label>Вид рекламы</label>
              <div className="checkbox-group-horizontal">
                <label className="checkbox-label">
                  <input 
                    type="checkbox" 
                    name="ad_types.self_promotion"
                    checked={formData.ad_types.self_promotion}
                    onChange={handleChange}
                  />
                  Самореклама
                </label>
                <label className="checkbox-label">
                  <input 
                    type="checkbox" 
                    name="ad_types.social_ad_quota"
                    checked={formData.ad_types.social_ad_quota}
                    onChange={handleChange}
                  />
                  Социальная реклама по квоте
                </label>
                <label className="checkbox-label">
                  <input 
                    type="checkbox" 
                    name="ad_types.social_ad"
                    checked={formData.ad_types.social_ad}
                    onChange={handleChange}
                  />
                  Социальная реклама
                </label>
                <label className="checkbox-label">
                  <input 
                    type="checkbox" 
                    name="ad_types.cobranding"
                    checked={formData.ad_types.cobranding}
                    onChange={handleChange}
                  />
                  Кобрендинг
                </label>
              </div>
            </div>

            {/* Описание - редактируемое */}
            <div className="form-group">
              <label>Описание</label>
              <textarea 
                name="description"
                value={formData.description}
                onChange={handleChange}
                className="form-control"
                rows="3"
              />
            </div>

            {/* Бренд - редактируемое */}
            <div className="form-group">
              <label>Бренд</label>
              <input 
                type="text" 
                name="brand"
                value={formData.brand}
                onChange={handleChange}
                className="form-control"
              />
            </div>

            {/* Категория - редактируемое */}
            <div className="form-group">
              <label>Категория</label>
              <input 
                type="text" 
                name="category"
                value={formData.category}
                onChange={handleChange}
                className="form-control"
              />
            </div>

            {/* Целевая аудитория - редактируемое */}
            <div className="form-group">
              <label>Целевая аудитория</label>
              <input 
                type="text" 
                name="target_audience"
                value={formData.target_audience}
                onChange={handleChange}
                className="form-control"
                placeholder="мж 6-50"
              />
            </div>

            {/* Текст креатива - редактируемое */}
            <div className="form-group">
              <label>Текст креатива</label>
              <textarea 
                name="creative_text"
                value={formData.creative_text}
                onChange={handleChange}
                className="form-control"
                rows="4"
                placeholder="Текстовое содержимое креатива..."
              />
            </div>

            {/* Тип креатива - редактируемое поле */}
            <div className="form-group">
              <label>Тип креатива</label>
              <select
                name="form"
                value={formData.form || ''}
                onChange={handleChange}
                className="form-control"
                key={`form-select-${formData.form || 'empty'}`}
                title={(() => {
                  const selectedType = creativeTypes.find(t => t.code === formData.form);
                  return selectedType?.description || selectedType?.name || '';
                })()}
              >
                <option value="">Выберите тип креатива</option>
                {Array.isArray(creativeTypes) && creativeTypes.map(type => (
                  <option 
                    key={type.code} 
                    value={type.code}
                    title={type.description || type.name}
                  >
                    {type.name}
                  </option>
                ))}
              </select>
              {formData.form && (() => {
                const selectedType = creativeTypes.find(t => t.code === formData.form);
                if (!selectedType) {
                  return (
                    <div style={{ color: 'red', fontSize: '12px', marginTop: '5px' }}>
                      ⚠️ Значение "{formData.form}" не найдено в справочнике
                    </div>
                  );
                }
                if (selectedType.description && selectedType.description !== selectedType.name) {
                  return (
                    <div style={{ color: '#666', fontSize: '12px', marginTop: '5px', fontStyle: 'italic' }}>
                      💡 {selectedType.description}
                    </div>
                  );
                }
                return null;
              })()}
            </div>

            {/* Код ККТУ - редактируемое поле */}
            <div className="form-group">
              <label>Код ККТУ</label>
              <select
                name="kktu_code"
                value={formData.kktu_code}
                onChange={handleChange}
                className="form-control"
              >
                <option value="">Выберите код ККТУ</option>
                {Array.isArray(kktuList) && kktuList.map(kktu => (
                  <option key={kktu.code} value={kktu.code}>
                    {kktu.code} - {kktu.name}
                  </option>
                ))}
              </select>
            </div>

            {/* Договор - только для чтения с ссылкой */}
            <div className="form-group">
              <label>Изначальный договор (только чтение)</label>
              <div className="contract-info">
                {(() => {
                  if (!formData.contract_external_id) return <span>Не указан</span>;
                  
                  const contract = contracts.find(c => c.external_id === formData.contract_external_id);
                  if (!contract) {
                    // Если договор не найден в списке - показываем код
                    return <span>{formData.contract_external_id}</span>;
                  }
                  
                  // ✅ Формат: "№ 23 - Реклама Единый фитнес" (номер договора + комментарий в одну строку)
                  const displayName = contract.serial 
                    ? `№ ${contract.serial}${contract.comment ? ' - ' + contract.comment : ''}`
                    : (contract.comment || contract.external_id);
                  
                  return (
                    <span 
                      className="contract-link" 
                      onClick={() => window.open(`#contracts/${contract.id}`, '_blank')}
                      style={{ color: '#ff8c65', cursor: 'pointer', textDecoration: 'underline' }}
                    >
                      {displayName}
                    </span>
                  );
                })()}
              </div>
            </div>

            {/* Медиафайлы с иконками для видео */}
            {formData.media_files && formData.media_files.length > 0 && (
              <div className="form-group">
                <label>Медиафайлы ({formData.media_files.length})</label>
                <div style={{ 
                  display: 'grid', 
                  gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))',
                  gap: '15px',
                  marginTop: '10px'
                }}>
                  {formData.media_files.map((fileId, index) => (
                    <div key={index} style={{ 
                      position: 'relative',
                      width: '100%',
                      paddingTop: '100%',  // Квадратный контейнер
                      backgroundColor: '#f5f5f5',
                      borderRadius: '8px',
                      overflow: 'hidden',
                      border: '1px solid #e0e0e0'
                    }}>
                      <img
                        src={getMediaUrl(fileId)}
                        alt={`Медиафайл ${index + 1}`}
                        onError={() => handleImageError(fileId)}
                        style={{ 
                          position: 'absolute',
                          top: 0,
                          left: 0,
                          width: '100%',
                          height: '100%',
                          objectFit: 'cover',  // Обрезка по квадрату
                          display: imageErrors[fileId] ? 'none' : 'block'  // Скрываем если ошибка
                        }}
                      />
                      
                      {/* Placeholder для видео если изображение не загрузилось */}
                      {imageErrors[fileId] && (
                        <div style={{
                          position: 'absolute',
                          top: 0,
                          left: 0,
                          width: '100%',
                          height: '100%',
                          backgroundColor: '#2c2c2c',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center'
                        }}>
                          <svg width="64" height="64" viewBox="0 0 24 24" fill="#ffffff" opacity="0.5">
                            <path d="M17 10.5V7c0-.55-.45-1-1-1H4c-.55 0-1 .45-1 1v10c0 .55.45 1 1 1h12c.55 0 1-.45 1-1v-3.5l4 4v-11l-4 4z"/>
                          </svg>
                        </div>
                      )}
                      {/* Иконка play для видео */}
                      {isVideoFile(fileId) && (
                        <div style={{
                          position: 'absolute',
                          top: '50%',
                          left: '50%',
                          transform: 'translate(-50%, -50%)',
                          width: '60px',
                          height: '60px',
                          backgroundColor: 'rgba(0, 0, 0, 0.6)',
                          borderRadius: '50%',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          pointerEvents: 'none',
                          zIndex: 2
                        }}>
                          <svg width="28" height="28" viewBox="0 0 24 24" fill="white" style={{ marginLeft: '3px' }}>
                            <path d="M8 5v14l11-7z"/>
                          </svg>
                        </div>
                      )}
                      
                      {/* Overlay с информацией */}
                      <div style={{
                        position: 'absolute',
                        bottom: 0,
                        left: 0,
                        right: 0,
                        padding: '8px',
                        background: 'linear-gradient(to top, rgba(0,0,0,0.7), transparent)',
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                        zIndex: 1
                      }}>
                        <span style={{ 
                          fontSize: '11px', 
                          color: '#fff',
                          fontFamily: 'monospace'
                        }}>
                          {fileId.substring(0, 8)}...
                        </span>
                        <a 
                          href={getMediaUrl(fileId)} 
                          target="_blank" 
                          rel="noopener noreferrer"
                          style={{
                            color: '#fff',
                            fontSize: '11px',
                            textDecoration: 'underline',
                            cursor: 'pointer'
                          }}
                        >
                          Открыть
                        </a>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            <div className="modal-actions" style={{ display: 'flex', gap: '12px', justifyContent: 'flex-end' }}>
              <button type="button" className="btn btn-secondary" onClick={onClose}>
                Отмена
              </button>
              <button type="submit" className="btn btn-primary">
                Сохранить изменения
              </button>
            </div>

            {/* Ссылки на посты в соцсетях */}
            <div className="form-group" style={{ marginTop: '32px' }}>
              <label>Ссылка на пост в Telegram</label>
              <input
                type="text"
                name="telegram_post_url"
                value={formData.telegram_post_url || ''}
                onChange={handleChange}
                placeholder="https://t.me/channel/123"
                className="form-control"
              />
            </div>

            {/* Кнопка получения просмотров */}
            {formData.telegram_post_url && (
              <div className="form-group">
                <button 
                  type="button" 
                  onClick={handleGetViews} 
                  disabled={loadingViews}
                  className="btn btn-secondary"
                  style={{ marginTop: '10px', display: 'flex', alignItems: 'center', gap: '8px' }}
                >
                  {loadingViews ? (
                    <>
                      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" style={{ animation: 'spin 1s linear infinite' }}>
                        <circle cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="3" strokeOpacity="0.3"/>
                        <path d="M12 2a10 10 0 0 1 10 10" stroke="currentColor" strokeWidth="3" strokeLinecap="round"/>
                      </svg>
                      Загрузка...
                    </>
                  ) : (
                    <>
                      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                        <path d="M3 13h8V3H3v10zm0 8h8v-6H3v6zm10 0h8V11h-8v10zm0-18v6h8V3h-8z" fill="currentColor"/>
                      </svg>
                      Получить просмотры
                    </>
                  )}
                </button>
              </div>
            )}

            {/* Отображение статистики просмотров */}
            {viewsData && (
              <div className="form-group" style={{ 
                marginTop: '16px', 
                padding: '16px', 
                backgroundColor: '#f9fafb',
                border: '1px solid #e5e7eb',
                borderRadius: '8px'
              }}>
                <h4 style={{ 
                  marginBottom: '12px', 
                  color: '#1f2937',
                  fontSize: '13px',
                  fontWeight: '600',
                  textTransform: 'uppercase',
                  letterSpacing: '0.05em'
                }}>Статистика просмотров</h4>
                
                {viewsData.telegram_views && !viewsData.telegram_views.error && (
                  <div style={{ marginBottom: '16px' }}>
                    <div style={{ 
                      fontSize: '11px',
                      color: '#6b7280',
                      marginBottom: '8px',
                      fontWeight: '500',
                      textTransform: 'uppercase',
                      letterSpacing: '0.05em'
                    }}>Telegram</div>
                    <div style={{ 
                      display: 'grid', 
                      gridTemplateColumns: 'repeat(3, 1fr)', 
                      gap: '10px'
                    }}>
                      <div>
                        <div style={{ fontSize: '22px', fontWeight: '600', color: '#111827' }}>
                          {viewsData.telegram_views.views?.toLocaleString() || 0}
                        </div>
                        <div style={{ fontSize: '11px', color: '#9ca3af', marginTop: '2px' }}>
                          Просмотры
                        </div>
                      </div>
                      <div>
                        <div style={{ fontSize: '22px', fontWeight: '600', color: '#111827' }}>
                          {viewsData.telegram_views.forwards?.toLocaleString() || 0}
                        </div>
                        <div style={{ fontSize: '11px', color: '#9ca3af', marginTop: '2px' }}>
                          Пересылки
                        </div>
                      </div>
                      <div>
                        <div style={{ fontSize: '22px', fontWeight: '600', color: '#111827' }}>
                          {viewsData.telegram_views.replies?.toLocaleString() || 0}
                        </div>
                        <div style={{ fontSize: '11px', color: '#9ca3af', marginTop: '2px' }}>
                          Ответы
                        </div>
                      </div>
                    </div>
                    {viewsData.telegram_views.channel_title && (
                      <div style={{ 
                        marginTop: '8px', 
                        fontSize: '11px', 
                        color: '#9ca3af',
                        fontStyle: 'italic'
                      }}>
                        Канал: {viewsData.telegram_views.channel_title}
                      </div>
                    )}
                  </div>
                )}

                {viewsData.telegram_views?.error && (
                  <div style={{ 
                    color: '#dc2626',
                    fontSize: '12px',
                    marginBottom: '12px',
                    padding: '8px',
                    backgroundColor: '#fef2f2',
                    borderRadius: '4px'
                  }}>
                    Telegram: {viewsData.telegram_views.error}
                  </div>
                )}

                {viewsData.vk_views && !viewsData.vk_views.error && (
                  <div>
                    <div style={{ 
                      fontSize: '11px',
                      color: '#6b7280',
                      marginBottom: '8px',
                      fontWeight: '500',
                      textTransform: 'uppercase',
                      letterSpacing: '0.05em'
                    }}>ВКонтакте</div>
                    <div style={{ 
                      display: 'grid', 
                      gridTemplateColumns: 'repeat(4, 1fr)', 
                      gap: '10px'
                    }}>
                      <div>
                        <div style={{ fontSize: '22px', fontWeight: '600', color: '#111827' }}>
                          {viewsData.vk_views.views?.toLocaleString() || 0}
                        </div>
                        <div style={{ fontSize: '11px', color: '#9ca3af', marginTop: '2px' }}>
                          Просмотры
                        </div>
                      </div>
                      <div>
                        <div style={{ fontSize: '22px', fontWeight: '600', color: '#111827' }}>
                          {viewsData.vk_views.likes?.toLocaleString() || 0}
                        </div>
                        <div style={{ fontSize: '11px', color: '#9ca3af', marginTop: '2px' }}>
                          Лайки
                        </div>
                      </div>
                      <div>
                        <div style={{ fontSize: '22px', fontWeight: '600', color: '#111827' }}>
                          {viewsData.vk_views.reposts?.toLocaleString() || 0}
                        </div>
                        <div style={{ fontSize: '11px', color: '#9ca3af', marginTop: '2px' }}>
                          Репосты
                        </div>
                      </div>
                      <div>
                        <div style={{ fontSize: '22px', fontWeight: '600', color: '#111827' }}>
                          {viewsData.vk_views.comments?.toLocaleString() || 0}
                        </div>
                        <div style={{ fontSize: '11px', color: '#9ca3af', marginTop: '2px' }}>
                          Комментарии
                        </div>
                      </div>
                    </div>
                  </div>
                )}

                {viewsData.vk_views?.error && (
                  <div style={{ 
                    color: '#dc2626',
                    fontSize: '12px',
                    padding: '8px',
                    backgroundColor: '#fef2f2',
                    borderRadius: '4px'
                  }}>
                    VK: {viewsData.vk_views.error}
                  </div>
                )}
              </div>
            )}
          </form>
          )}
        </div>
      </div>
    </div>
  );
};

export default EditCreativeModal;
